import React from 'react';
import { Row, Col, Card , Table} from 'reactstrap';
import CodeBlock from '../CodeBlock';
import { HashLink as Link } from 'react-router-hash-link';

function ErrorCodeSection() {
    return (
        <section id="errorcodes">
            <Row>
                <Col>
                    <h2>Error codes and responses</h2>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h3 id="errorstatuscodes">HTTP Status Codes (API wide)</h3>
                    <p>API returns appropriate HTTP status codes for every request.</p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Http Status Code</th>
                                <th>Reason phrase</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>200 (OK)</td>
                                <td></td>
                                <td>Success</td>
                            </tr>
                            <tr>
                                <td>400 (Bad Request)</td>
                                <td></td>
                                <td>
                                    The request was invalid or cannot be otherwise served. An accompanying error message will explain
                                    further.
                                </td>
                            </tr>
                            <tr>
                                <td>401 (Unauthorized)</td>
                                <td>Token expired</td>
                                <td>Token used for authorisation has expired, new token should be requested.</td>
                            </tr>
                            <tr>
                                <td>403 (Forbidden)</td>
                                <td>You do not have a CV Database contract</td>
                                <td>
                                    A valid CV Database contract that is required to use the API was not found. Does not apply to
                                    authentication API.
                                </td>
                            </tr>
                            <tr>
                                <td>429 (Too Many Requests)</td>
                                <td></td>
                                <td>You have exceeded our request rate limit threshold</td>
                            </tr>
                            <tr>
                                <td>500 (Internal Server Error)</td>
                                <td></td>
                                <td>
                                    The server encountered an unexpected condition which prevented it from fulfilling the request.
                                    Response contains additional property - &quot;error_id&quot; which can be used to troubleshoot
                                    error on TJG side.
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                    <p>
                        Only HTTP status code – 500 – should be treated as server error. Rest of the status codes should be
                        handled by API client.
                    </p>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h3 id="errorstatuscodesspecific">HTTP Status Codes (CV endpoint specific)</h3>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Http Status Code</th>
                                <th>Reason phrase</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>403 (Forbidden)</td>
                                <td>Max ids limit reached</td>
                                <td>
                                    CV download limit has been reached for specific recruitment company.
                                    <code>{`{"Message": "Cv downloads limit reached"}`}</code>.
                                </td>
                            </tr>
                            <tr>
                                <td>403 (Forbidden)</td>
                                <td>Downloads limit reached</td>
                                <td>
                                    Allowed number of requested Cv’s (10) has been exceeded.
                                    <code>{`{"Message": "10 candidateIds limit reached"}`}</code>.
                                </td>
                            </tr>
                            <tr>
                                <td>403 (Forbidden)</td>
                                <td>Monthly Limit reached</td>
                                <td>
                                    Exceeded the maximum credits allowed in a month.
                                    <code>{`{"Message": "You have exceeded the maximum credits allowed in a month. If you require further credits, please contact your company administrator or the [TJG Brand] customer services team - [Customer service number]"}`}</code>.
                                </td>
                            </tr>
                            <tr>
                                <td>404 (Not found)</td>
                                <td></td>
                                <td>CV for specified candidate is not available.</td>
                            </tr>
                        </tbody>
                    </Table>
                    <p>
                        Only HTTP status code – 500 – can be treated as server error. Rest of the status codes should be handled
                        by API client.
                    </p>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h3 id="errormessages">Error messages</h3>
                    <p>API returns error messages in JSON format. For example, an error might look like this:</p>
                    <Card className="bg-dark text-white" body>
                        <h4>ERROR</h4>
                        <CodeBlock langauge="json" theme="dark">
                            {`{\n\t"error": "invalid_grant",\n\t"error_description": "Recruiter name or password is incorrect"\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h3 id="oautherror">OAuth error messages</h3>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Error</th>
                                <th>Description</th>
                                <th>Http Status Code</th>
                                <th>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td rowSpan={4}>invalid_request</td>
                                <td>
                                    <code>client_id</code> is empty
                                </td>
                                <td>400</td>
                                <td>
                                    <code>client_id</code> has not been provided.
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <code>client_secret</code> is empty
                                </td>
                                <td>400</td>
                                <td>
                                    <code>client_secret</code> has not been provided.
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Recruiter <code>username</code> is empty{' '}
                                </td>
                                <td>400</td>
                                <td>
                                    <code>username</code> has not been provided.
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Recruiter <code>password</code> is empty
                                </td>
                                <td>400</td>
                                <td>
                                    <code>password</code> has not been provided.
                                </td>
                            </tr>
                            <tr>
                                <td rowSpan={3}>invalid_grant</td>
                                <td>Limit of unsuccessful recruiter login attempts reached</td>
                                <td>400</td>
                                <td>
                                    Recruiter account has been blocked due to exceeding the number of incorrect logins (8). 
                                    On the 9<sup>th</sup> attempt an email will be sent to the recruiter informing them a password reset is required.
                                </td>
                            </tr>
                            <tr>
                                <td>Recruiter account is not entitled to access requested scope</td>
                                <td>400</td>
                                <td>Recruiter does not have privileges to access requested data.</td>
                            </tr>
                            <tr>
                                <td>Recruiter name or password is incorrect</td>
                                <td>400</td>
                                <td></td>
                            </tr>
                            <tr>
                                <td>unsupported_grant_type</td>
                                <td></td>
                                <td>400</td>
                                <td>
                                    <code>grant_type</code> has not been provided or has incorrect value.
                                </td>
                            </tr>
                            <tr>
                                <td>invalid_scope</td>
                                <td></td>
                                <td>400</td>
                                <td>
                                    <code>scope</code> has not been provided or has incorrect value.
                                </td>
                            </tr>
                            <tr>
                                <td>invalid_client</td>
                                <td>
                                    <code>client_id</code> or <code>client_secret</code> is incorrect
                                </td>
                                <td>400</td>
                                <td></td>
                            </tr>
                            <tr>
                                <td>server_error</td>
                                <td>We were unable to process the request</td>
                                <td>500</td>
                                <td>
                                    Response contains additional property - <code>error_id</code> which can be used to troubleshoot
                                    error on TJG side.{' '}
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                    <p>
                        If you see an error response which is not listed in the above table, then fall back to the HTTP status
                        code in order to determine the best way to address the error.
                    </p>
                    <h4 id="searchvalidation">Search endpoint validation messages</h4>
                    <p>
                        Every request to Search endpoint is being checked in order to validate correctness of search filters.
                    </p>
                    <p>
                        In case of an incorrect request the search endpoint will return appropriate error message allowing improved
                        troubleshooting on the issue.
                    </p>
                    <p>
                        Search endpoint return validation messages in JSON format. For example, a message might look like this:
                    </p>
                    <Card className="bg-dark text-white" body>
                        <h4>ERROR</h4>
                        <CodeBlock langauge="json" theme="dark">
                            {`{\n\t"Message": "The request is invalid.",\n\t"ModelState": {\n\t\t"searchCriteriaModel.Page": [\n\t\t\t"Property Page is required"\n\t\t],\n\t\t"searchCriteriaModel.SearchType": [\n\t\t\t"Property SearchType is required"\n\t\t]\n\t}\n}`}
                        </CodeBlock>
                    </Card>
                    <p>Most common validation messages&apos; types.</p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Validation issue</th>
                                <th>Sample message</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Required field missing</td>
                                <td>
                                    Property {`{property_name}`} is required <br />
                                    <code>Property SearchType is required</code>
                                </td>
                            </tr>
                            <tr>
                                <td>Field value is not allowed</td>
                                <td>
                                    <p>
                                        Property {`{property_value}`} is not allowed for {`{property_name}`}
                                    </p>
                                    <ul className="list-group">
                                        <li className="list-group-item border-0 p-1">
                                            <code>Value &apos;JobTitle&apos; is not allowed for Sort.Column</code>
                                        </li>
                                        <li className="list-group-item border-0 p-1">
                                            <code>Value &apos;English_typo&apos; is not allowed for Languages</code>
                                        </li>
                                        <li className="list-group-item border-0 p-1">
                                            <code>Value &apos;01.11.2015 00:00:00&apos; is not allowed for LastActivityDate</code>
                                        </li>
                                    </ul>
                                </td>
                            </tr>
                            <tr>
                                <td>Brand specific filter used in for incorrect brand</td>
                                <td>
                                    Property {`{property_name}`} is not allowed <code>site_name</code>
                                    <br />
                                    <code>Using CareerStructureJobRole is not allowed for TotalJobs</code>
                                </td>
                            </tr>
                            <tr>
                                <td>PostcodeDistance and DesiredLocation filter used in the same request</td>
                                <td>
                                    <code>Either DesiredLocations or PostcodeDistance can be used</code>
                                </td>
                            </tr>
                            <tr>
                                <td>Postcode has incorrect format</td>
                                <td>
                                    <code>Please enter a valid postcode e.g. W1T 1JU</code>
                                </td>
                            </tr>
                            <tr>
                                <td>Postcode cannot be found</td>
                                <td>
                                    <code>The postcode you entered cannot be found</code>
                                </td>
                            </tr>
                            <tr>
                                <td>Obsolete and current version of parameter in the same request</td>
                                <td>
                                    <code>
                                        Obsolete search criterium JobType can not be used once new version of this criterium
                                        JobTypeProfile is used.
                                    </code>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                    <h4 id="viewlimiterror">Credits limit error messages</h4>
                    <p>
                        From version 6.5 there is a candidate Credits limit introduced in CandidateSearch API. <b>Credit</b> is every
                        request for candidate data which results in a candidate&apos;s contact data being returned in
                        response. Credits limit cap is daily and individual for each Recruiter account, dependent upon their license
                        type.
                    </p>
                    <p>Currently affected endpoints are:</p>
                    <ul className="list-group">
                        <li className="list-group-item border-0 p-1">
                            <Link to="#endpointscandidatedetails">Candidates Details endpoint</Link>
                        </li>
                        <li className="list-group-item border-0 p-1">
                            <Link to="#cvpreview">Preview CV endpoint</Link>
                        </li>
                        <li className="list-group-item border-0 p-1">
                            <Link to="#cvdownload">Download CV endpoint</Link>
                        </li>
                    </ul>
                    <p>
                        In case of daily requests limit being hit, following error messages are returned in response content:
                    </p>
                    <p>
                        <b>For non-trial accounts</b>
                    </p>
                    <p>
                        <code>
                            You have exceeded the maximum candidate credits allowed in a 24hr period. If you require further credits,
                            please contact the [BRAND] customer services team - 0333 0145 111.
                        </code>
                    </p>
                    <p>
                        <b>For trial account</b>
                    </p>
                    <p>
                        <code>
                            Your trial has exceeded the maximum candidate credits in a 24hr period. If you require further credits,
                            please contact your [BRAND] account manager.
                            <br />
                            [BRAND] is Name of brand determined by endpoint we send request to.
                        </code>
                    </p>
                </Col>
            </Row>
        </section>
    )
}

export default ErrorCodeSection
